import { makeStyles } from '@material-ui/core';
import { DetailsHTMLAttributes, ReactNode } from 'react';
import { clsx, theme, colorSet } from '@components/Provider';

const typography = theme['typography'];
const colors = colorSet;

interface CoreProps extends DetailsHTMLAttributes<HTMLElement> {
  children?: ReactNode;
  onClick?: () => void;
}
type WrapperProps = CoreProps;

interface HeaderProps extends CoreProps {
  color?: 'primary' | 'error';
  createdAt?: string;
}

interface TextProps extends CoreProps {
  variant?: keyof typeof typography;
  color?: keyof typeof colors;
  fontWeight?: '400' | '500';
  gutterBottom?: number;
  as?: keyof JSX.IntrinsicElements;
}

const Wrapper = ({ children, onClick }: WrapperProps) => {
  const classes = useStyles();
  return (
    <div className={classes.wrapper} onClick={onClick}>
      {children}
    </div>
  );
};

const Header = ({
  color = 'primary',
  children = 'label',
  createdAt
}: HeaderProps) => {
  const classes = useStyles();

  return (
    <div className={classes.header}>
      <div
        className={clsx('chip', {
          'primary': color === 'primary',
          'error': color === 'error'
        })}
      >
        <Text
          as="span"
          variant="caption"
          color={color === 'primary' ? 'primary500' : 'errorMain'}
        >
          {children}
        </Text>
      </div>
      <Text color="gray500" variant="subtitle2" fontWeight="400">
        {createdAt}
      </Text>
    </div>
  );
};

const Text = ({
  variant,
  children,
  fontWeight = '400',
  color,
  className,
  gutterBottom,
  as: Component = 'p',
  style
}: TextProps) => {
  const classes = useStyles();
  return (
    <Component
      style={{
        ...style,
        fontWeight,
        color: colorSet[color as keyof typeof colorSet],
        marginBottom: gutterBottom
      }}
      className={clsx(
        className,
        classes.text,
        classes[variant as keyof typeof classes]
      )}
    >
      {children}
    </Component>
  );
};

export default Object.assign(Wrapper, {
  Header,
  Text
});

const useStyles = makeStyles(
  {
    wrapper: {
      padding: 16,
      cursor: 'pointer',
      transition: 'background 0.2s linear',
      borderBottom: `1px solid ${colorSet['gray200']}`,
      '&:hover': {
        backgroundColor: 'rgba(244, 248, 255,0.7)'
      }
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 16,
      '& .chip': {
        padding: '4px 8px',
        borderRadius: 8,
        display: 'flex',
        '&.primary': {
          color: colorSet['primary500'],
          backgroundColor: colorSet['primary100']
        },
        '&.error': {
          color: colorSet['errorMain'],
          backgroundColor: colorSet['errorBackGround']
        }
      }
    },
    text: {
      wordBreak: 'break-all'
    },
    body1: typography['body1'],
    body2: typography['body2'],
    subtitle2: typography['subtitle2'],
    caption: typography['caption']
  },
  {
    classNamePrefix: 'notification'
  }
);
